import { useCallback, useEffect, useState } from 'react'
import { api, ApiFailure, downloadFile, type BackupFile } from '../../api'
import { Alert, Badge, Empty, Icon, Kpi, Modal, P, PageHeader, ago } from './ui'

/* Whole-server backups: take, download, restore, delete. One backup holds
   every clinic, which is why only the platform admin sees this. */

function size(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} kB`
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`
}

export default function Backups() {
  const [backups, setBackups] = useState<BackupFile[] | null>(null)
  const [directory, setDirectory] = useState('')
  const [busy, setBusy] = useState(false)
  const [msg, setMsg] = useState<{ kind: 'ok' | 'error' | 'warn'; text: string } | null>(null)
  const [restore, setRestore] = useState<BackupFile | null>(null)
  const [remove, setRemove] = useState<BackupFile | null>(null)
  const [typed, setTyped] = useState('')

  const load = useCallback(async () => {
    const r = await api.get<{ backups: BackupFile[]; directory: string }>('/api/backups')
    setBackups(r.backups); setDirectory(r.directory)
  }, [])
  useEffect(() => { void load().catch((e: Error) => { setMsg({ kind: 'error', text: e.message }); setBackups([]) }) }, [load])

  async function run(fn: () => Promise<void>, fail: string) {
    setBusy(true); setMsg(null)
    try { await fn() } catch (err) { setMsg({ kind: 'error', text: err instanceof ApiFailure ? err.message : fail }) } finally { setBusy(false) }
  }

  const latest = backups?.[0]
  const total = (backups ?? []).reduce((n, b) => n + b.sizeBytes, 0)
  const stale = latest ? Date.now() - new Date(latest.modifiedAt).getTime() > 2 * 86400_000 : true

  return (
    <>
      <PageHeader crumbs={['Platform', 'Operations', 'Backups']} title="Backups"
        description={<>A backup is the whole server, every clinic. Test a restore every quarter: a backup nobody has restored is a guess.</>}
        actions={<button type="button" className="pa-btn pa-btn-primary" disabled={busy}
          onClick={() => void run(async () => {
            const r = await api.post<{ filename: string; sizeBytes: number }>('/api/backups')
            setMsg({ kind: 'ok', text: `Backup written: ${r.filename} (${size(r.sizeBytes)}).` })
            await load()
          }, 'Backup failed.')}><Icon d={P.db} />{busy ? 'Working…' : 'Take a backup now'}</button>} />
      {msg && <Alert kind={msg.kind} onClose={() => setMsg(null)}>{msg.text}</Alert>}
      {backups && stale && <Alert kind="warn">{latest ? `The newest backup here is ${ago(latest.modifiedAt)}.` : 'There are no backups in this folder yet.'} The server’s encrypted nightly backup runs separately and is not listed here.</Alert>}

      <div className="pa-grid-kpi">
        <Kpi label="Backups here" icon={P.db} tone="primary" value={backups ? backups.length : '—'} note="In this folder" />
        <Kpi label="Latest" icon={P.refresh} tone={stale ? 'warning' : 'success'} value={latest ? ago(latest.modifiedAt) : '—'} note={latest ? size(latest.sizeBytes) : 'None yet'} />
        <Kpi label="Space used" icon={P.download} tone="info" value={backups ? size(total) : '—'} note="All files together" />
        <Kpi label="Covers" icon={P.shield} tone="neutral" value="All clinics" note="One file per backup" />
      </div>

      <div className="pa-card">
        <div className="pa-card-head"><div><h3>Files</h3><p>Written to <span className="pa-code">{directory || '—'}</span></p></div></div>
        <div className="pa-table-wrap">
          <table className="pa-table">
            <thead><tr><th>File</th><th>Taken</th><th className="num">Size</th><th style={{ width: 1 }}></th></tr></thead>
            <tbody>
              {backups === null && [0, 1].map((i) => <tr key={i}><td colSpan={4}><div className="pa-skel" style={{ height: 20 }} /></td></tr>)}
              {backups?.map((b, i) => (
                <tr key={b.name}>
                  <td><div className="pa-cell"><span className="pa-avatar t-neutral"><Icon d={P.db} size={15} /></span><div><b className="pa-code" style={{ fontWeight: 600 }}>{b.name}</b>{i === 0 && <small><Badge tone="success">Newest</Badge></small>}</div></div></td>
                  <td className="muted" title={new Date(b.modifiedAt).toLocaleString('en-IN')} style={{ whiteSpace: 'nowrap' }}>{ago(b.modifiedAt)}</td>
                  <td className="num">{size(b.sizeBytes)}</td>
                  <td style={{ whiteSpace: 'nowrap' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'flex-end' }}>
                      <button type="button" className="pa-btn pa-btn-sm" onClick={() => void run(() => downloadFile(`/api/backups/${encodeURIComponent(b.name)}/download`, b.name), 'Download failed.')}><Icon d={P.download} size={14} />Download</button>
                      <button type="button" className="pa-btn pa-btn-sm" onClick={() => { setTyped(''); setRestore(b) }}><Icon d={P.restore} size={14} />Restore</button>
                      <button type="button" className="pa-btn pa-btn-sm pa-icon-btn" aria-label={`Delete ${b.name}`} onClick={() => setRemove(b)}><Icon d={P.trash} size={14} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {backups?.length === 0 && <tr><td colSpan={4}><Empty icon={P.db} title="No backups here yet">Take one now. It covers every clinic on this server.</Empty></td></tr>}
            </tbody>
          </table>
        </div>
      </div>

      {restore && (
        <Modal title="Restore from backup" onClose={() => setRestore(null)}
          description={<>This wipes the current database, every clinic on this server, and replays <span className="pa-code">{restore.name}</span>. Everyone will need to sign in again.</>}
          footer={<>
            <button type="button" className="pa-btn" onClick={() => setRestore(null)}>Cancel</button>
            <button type="button" className="pa-btn pa-btn-danger" disabled={busy || typed !== 'RESTORE'}
              onClick={() => void run(async () => {
                const r = await api.post<{ ok: boolean }>(`/api/backups/${encodeURIComponent(restore.name)}/restore`, { confirm: 'RESTORE' })
                if (r.ok) { setMsg({ kind: 'ok', text: `Restored from ${restore.name}. You may need to sign in again.` }); setRestore(null); await load() }
              }, 'Restore failed.')}>Restore now</button>
          </>}>
          <div className="pa-field">
            <label htmlFor="pa-conf">Type <span className="pa-code">RESTORE</span> to confirm</label>
            <input id="pa-conf" className="pa-input pa-code" autoFocus value={typed} onChange={(e) => setTyped(e.target.value)} />
          </div>
        </Modal>
      )}

      {remove && (
        <Modal title="Delete this backup file?" onClose={() => setRemove(null)}
          description={<><span className="pa-code">{remove.name}</span> is removed from this folder. The database is not touched.</>}
          footer={<>
            <button type="button" className="pa-btn" onClick={() => setRemove(null)}>Cancel</button>
            <button type="button" className="pa-btn pa-btn-danger" disabled={busy}
              onClick={() => void run(async () => { await api.del(`/api/backups/${encodeURIComponent(remove.name)}`); setRemove(null); await load() }, 'Delete failed.')}>Delete file</button>
          </>}>
          <span />
        </Modal>
      )}
    </>
  )
}
